'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

const SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? '';

type RecaptchaEnterprise = {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

declare global {
  interface Window {
    grecaptcha?: {
      ready?: (cb: () => void) => void;
      execute?: (siteKey: string, options: { action: string }) => Promise<string>;
      enterprise?: RecaptchaEnterprise;
    };
  }
}

function getRecaptchaApi(): RecaptchaEnterprise | null {
  if (typeof window === 'undefined') return null;
  return window.grecaptcha?.enterprise ?? null;
}

function whenRecaptchaReady(onReady: () => void) {
  const api = getRecaptchaApi();
  if (api) {
    api.ready(onReady);
    return;
  }

  // Legacy loader (api.js) — keep for backwards compatibility.
  window.grecaptcha?.ready?.(onReady);
}

export function useRecaptcha() {
  const [ready, setReady] = useState(() => {
    if (!SITE_KEY) return true;
    if (typeof window === 'undefined') return false;
    const e2eToken = (window as { __E2E_RECAPTCHA_TOKEN__?: string }).__E2E_RECAPTCHA_TOKEN__;
    return typeof e2eToken === 'string' && e2eToken.length > 0;
  });
  const readyRef = useRef(ready);
  readyRef.current = ready;

  useEffect(() => {
    if (!SITE_KEY) {
      setReady(true);
      return;
    }

    // Playwright e2e stub — never wait on Google scripts / load events.
    const e2eToken = (window as { __E2E_RECAPTCHA_TOKEN__?: string }).__E2E_RECAPTCHA_TOKEN__;
    if (typeof e2eToken === 'string' && e2eToken.length > 0) {
      setReady(true);
      return;
    }

    const markReady = () => whenRecaptchaReady(() => setReady(true));

    const existing = document.getElementById('recaptcha-v3-script');
    if (existing) {
      if (getRecaptchaApi() || window.grecaptcha?.ready) {
        markReady();
      } else {
        existing.addEventListener('load', markReady, { once: true });
      }
      return;
    }

    const script = document.createElement('script');
    script.id = 'recaptcha-v3-script';
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = markReady;
    document.head.appendChild(script);
  }, []);

  const execute = useCallback(async (action: string): Promise<string> => {
    if (!SITE_KEY) return '';

    // Playwright e2e can pin a token without loading Google (avoids hang/flake).
    if (typeof window !== 'undefined') {
      const e2eToken = (window as { __E2E_RECAPTCHA_TOKEN__?: string }).__E2E_RECAPTCHA_TOKEN__;
      if (typeof e2eToken === 'string' && e2eToken.length > 0) return e2eToken;
    }

    // Wait briefly for the Enterprise API (or e2e stub) — avoids rejecting the
    // first submit while the script/effect is still marking ready.
    const deadline = Date.now() + 8_000;
    while (!getRecaptchaApi() && Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 50));
    }

    const api = getRecaptchaApi();
    if (!api) return '';
    try {
      // Real Google execute can hang when the script is mid-load; bound it.
      return await Promise.race([
        api.execute(SITE_KEY, { action }),
        new Promise<string>((resolve) => {
          window.setTimeout(() => resolve(''), 5_000);
        }),
      ]);
    } catch {
      return '';
    }
  }, []);

  const waitUntilReady = useCallback(async (timeoutMs = 8_000): Promise<boolean> => {
    if (!SITE_KEY) return true;
    if (typeof window !== 'undefined') {
      const e2eToken = (window as { __E2E_RECAPTCHA_TOKEN__?: string }).__E2E_RECAPTCHA_TOKEN__;
      if (typeof e2eToken === 'string' && e2eToken.length > 0) return true;
    }
    const deadline = Date.now() + timeoutMs;
    while (!readyRef.current && Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 50));
    }
    return readyRef.current || Boolean(getRecaptchaApi());
  }, []);

  return { ready, execute, waitUntilReady, isConfigured: Boolean(SITE_KEY) };
}

/**
 * Hides the reCAPTCHA Enterprise badge while the consuming component is mounted.
 *
 * Use this in authenticated layouts (e.g. `/app/*`, `/admin/*`) — the badge is
 * only meaningful on public forms (login, register, contact). Once the user is
 * inside the product shell we don't want the floating badge taking screen real
 * estate, and there's nothing to verify anyway.
 *
 * Restores visibility on unmount so the badge reappears when navigating back to
 * a public page (where reCAPTCHA Enterprise terms require the badge to be visible).
 */
export function useHideRecaptchaBadge(): void {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const apply = (hidden: boolean) => {
      const badge = document.querySelector<HTMLElement>('.grecaptcha-badge');
      if (!badge) return;
      badge.style.visibility = hidden ? 'hidden' : 'visible';
      badge.style.opacity = hidden ? '0' : '';
      badge.style.pointerEvents = hidden ? 'none' : '';
    };
    apply(true);
    // The badge may be injected asynchronously after the layout mounts (when a
    // child component calls `useRecaptcha`). Re-apply on a short interval until
    // we see it, then stop. Cheap and bounded.
    const interval = window.setInterval(() => {
      const badge = document.querySelector<HTMLElement>('.grecaptcha-badge');
      if (badge) {
        apply(true);
        window.clearInterval(interval);
      }
    }, 300);
    window.setTimeout(() => window.clearInterval(interval), 5000);
    return () => {
      window.clearInterval(interval);
      apply(false);
    };
  }, []);
}
